import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Home, MoreVertical } from "lucide-react";
import { Dialog, Modal, ModalOverlay } from "react-aria-components";
import type { AuthSession } from "../../../auth/keycloak.js";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { ThemeToggle } from "@/components/header/ThemeToggle";
import { ManualStateBadge } from "../ManualStateBadge.js";
import { useManualBody, useManualRatings } from "../useManualMutations.js";
import { ManualStartPage } from "./ManualStartPage.js";
import { InhaltTree } from "./InhaltTree.js";
import { StepView } from "./StepView.js";
import { RateOnFinishPanel } from "./RateOnFinishPanel.js";
import { ReportManualPanel } from "./ReportManualPanel.js";
import { ManualViewerRail, type ViewerPanel } from "./ManualViewerRail.js";
import { usePrintManual } from "../print/usePrintManual.js";
import { ManualPlatformGate } from "./ManualPlatformGate.js";
import { ManualPrintSheet } from "../print/ManualPrintSheet.js";

/**
 * The runtime manual viewer (ADR-002) — a full-screen reader opened from the details drawer's View button, over
 * react-aria's Modal (focus-trapped, Esc/backdrop dismiss, focus restored to the View button on close). Layout is a
 * persistent left **activity rail** (`ManualViewerRail`) + a swappable docked **side panel** (the Contents tree OR the
 * Report panel) + the main column (start page → step player → rate-on-finish). The rail is always visible; its icons
 * flip the side panel open/closed. Reads `GET /manuals/:id/body` (which resolves a linked child to its parent's body
 * server-side). The authoring editor (edit-mode rail tools) lands in Phase 10.
 */
export function ManualViewer({
  session,
  manualId,
  onClose,
  onExitToFacility,
  preview = false,
}: {
  session: AuthSession | null;
  manualId: number;
  /** Fallback exit — used when the manual's own facility isn't known yet (body still loading) or when no
   *  {@link onExitToFacility} is given (the classic local-state mount). */
  onClose: () => void;
  /** Preferred exit for the URL-routed (`?arc_manual`) mount: navigate to the manual's OWN facility manuals list — its id
   *  comes from the loaded body's facility path leaf. Lets t("viewer.backToOverview") return to the list the reader replaced,
   *  even on a cold deep-link where there is no history to go back to. */
  onExitToFacility?: (facilityId: number) => void;
  /** The editor's preview: authors must be able to check their work whatever the platforms (ADR-057). */
  preview?: boolean;
}) {
  const { t } = useTranslation();
  const body = useManualBody(session, manualId);
  const ratings = useManualRatings(session, manualId);
  const [started, setStarted] = useState(false);
  const [currentStepId, setCurrentStepId] = useState<number | null>(null);
  // The rail's swappable side panel — null = collapsed (rail only). Its state persists start → player.
  const [activePanel, setActivePanel] = useState<ViewerPanel | null>(null);
  // Reached the last step → the completion / rate-on-finish view (ADR-002 Phase 7).
  const [finished, setFinished] = useState(false);

  const allSteps = useMemo(() => body.data?.sections.flatMap((s) => s.steps) ?? [], [body.data]);
  useEffect(() => {
    if (started && currentStepId == null && allSteps.length > 0) setCurrentStepId(allSteps[0]!.id);
  }, [started, currentStepId, allSteps]);

  const currentIndex = allSteps.findIndex((s) => s.id === currentStepId);
  const currentStep = currentIndex >= 0 ? allSteps[currentIndex] : null;

  const hasContents = !!body.data && body.data.sections.length > 0;
  const canReport = !!body.data && body.data.capabilities.canReport && body.data.manual.state === "confirmed";
  // Print (ADR-031): a published, not-archived manual — the legacy's share-section gate; a linked child prints its
  // parent's body (the body read already resolves it). The sheet fetches a FRESH body (presigns), never this cache.
  const print = usePrintManual();
  const canPrint = !!body.data && (body.data.manual.state === "confirmed" || body.data.manual.state === "marked") && !body.data.archived;
  // Clicking the active icon collapses the panel; a different icon swaps the flyout's content (activity-bar model).
  const togglePanel = (panel: ViewerPanel) => setActivePanel((cur) => (cur === panel ? null : panel));

  // Exit → the manual's own facility manuals list when we can (URL-routed mount + body loaded); else the caller's fallback.
  const exitFacilityId = body.data?.facilityPath.at(-1)?.id ?? null;
  const handleExit = () => (onExitToFacility && exitFacilityId !== null ? onExitToFacility(exitFacilityId) : onClose());

  return (
    <ModalOverlay isDismissable isOpen onOpenChange={(open) => !open && handleExit()} className="bg-background fixed inset-0 z-50">
      <Modal className="h-full w-full outline-hidden">
        <Dialog aria-label={body.data ? body.data.manual.name : t("manual.raw")} className="flex h-full flex-col outline-hidden">
          {/* Two-row header: breadcrumb (home → facility path → category → manual) above the back button + title, with
              the status badge + more on the right. Contents + Report now live on the rail, not here. */}
          <header className="border-border flex flex-col gap-3 border-b px-5 py-4">
            {body.data ? (
              <nav aria-label={t("viewer.breadcrumb")} className="text-muted-foreground flex flex-wrap items-center gap-1.5 text-sm">
                <button
                  type="button"
                  onClick={handleExit}
                  aria-label={t("viewer.backToManuals")}
                  className="hover:text-foreground focus-visible:ring-ring/50 cursor-pointer rounded-sm border-0 bg-transparent p-0.5 outline-none focus-visible:ring-2"
                >
                  <Home aria-hidden="true" className="size-4" />
                </button>
                {body.data.facilityPath.map((f) => (
                  <span key={f.id} className="flex items-center gap-1.5">
                    <ChevronRight aria-hidden="true" className="size-3.5 opacity-60" />
                    <span className="max-w-[14rem] truncate">{f.name}</span>
                  </span>
                ))}
                <span className="flex items-center gap-1.5">
                  <ChevronRight aria-hidden="true" className="size-3.5 opacity-60" />
                  <span className="max-w-[14rem] truncate">{body.data.manual.categoryName}</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <ChevronRight aria-hidden="true" className="size-3.5 opacity-60" />
                  <span aria-current="page" className="text-foreground max-w-[18rem] truncate font-medium">
                    {body.data.manual.name}
                  </span>
                </span>
              </nav>
            ) : null}
            <div className="flex items-center justify-between gap-4">
              <div className="flex min-w-0 items-center gap-3">
                <Button variant="outline" size="lg" onPress={handleExit} className="dark:border-border shrink-0">
                  <ChevronLeft aria-hidden="true" />
                  {t("facilitySelection.nav.back")}
                </Button>
                <h1 className="min-w-0 truncate text-xl font-semibold">{body.data?.manual.name ?? "…"}</h1>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {body.data ? <ManualStateBadge state={body.data.manual.state} comment={body.data.manual.markedComment} /> : null}
                <span className="bg-border mx-1 h-6 w-px" aria-hidden="true" />
                {/* The viewer is full-screen with no global header, so the appearance control lives here (owner: switch
                    light/dark in the areas without the main header). Same tri-state control as the header. */}
                <ThemeToggle />
                <TooltipTrigger delay={0}>
                  <span className="inline-flex">
                    <Button variant="ghost" size="icon-lg" isDisabled aria-label={t("viewer.moreComingSoon")}>
                      <MoreVertical aria-hidden="true" />
                    </Button>
                  </span>
                  <Tooltip>{t("viewer.moreComingSoon2")}</Tooltip>
                </TooltipTrigger>
              </div>
            </div>
          </header>

          <div className="relative flex min-h-0 flex-1 overflow-hidden">
            <ManualViewerRail
              active={activePanel}
              onSelect={togglePanel}
              hasContents={hasContents}
              canReport={canReport}
              onPrint={canPrint ? () => print.start(manualId) : undefined}
              printing={print.printing !== null}
            />
            {print.body ? <ManualPrintSheet body={print.body} onReady={print.onReady} onAfterPrint={print.onAfterPrint} /> : null}
            {print.error ? (
              <p role="alert" className="bg-destructive text-destructive-foreground absolute bottom-3 left-16 z-40 rounded-md px-3 py-2 text-sm shadow-lg">
                {print.error}
              </p>
            ) : null}
            {/* The swappable side panel OVERLAYS the content (absolute) — it floats over it, it does NOT push it to the
                right. This keeps the reading area at full width and is the correct behaviour on mobile (a drawer over the
                page). Deliberately NON-MODAL (no blocking scrim): the content stays interactive so the reader can keep
                navigating steps (prev/Next) with the panel open. Toggle it shut from the rail icon (or Report's Cancel). */}
            {activePanel && body.data && (activePanel === "contents" ? hasContents : canReport) ? (
              <div className="animate-in slide-in-from-left-2 fade-in-0 bg-background absolute inset-y-0 left-14 z-30 flex w-80 max-w-[calc(100%-3.5rem)] shadow-xl duration-150">
                {activePanel === "contents" ? (
                  <InhaltTree
                    sections={body.data.sections}
                    currentStepId={currentStepId}
                    onSelectStep={(id) => {
                      setCurrentStepId(id);
                      setStarted(true);
                      setFinished(false);
                    }}
                  />
                ) : (
                  <ReportManualPanel
                    session={session}
                    manual={body.data.manual}
                    sections={body.data.sections}
                    currentStepId={currentStepId}
                    onClose={() => setActivePanel(null)}
                    onMarked={() => void body.refetch()}
                  />
                )}
              </div>
            ) : null}
            <div className="min-w-0 flex-1 overflow-auto">
              {body.isPending ? (
                <p className="text-muted-foreground p-6 text-sm">{t("common.loading")}</p>
              ) : body.isError || !body.data ? (
                <p className="text-destructive p-6 text-sm">{t("viewer.loadManualFailed")}</p>
              ) : !preview && !body.data.capabilities.platforms.is2dWebManual ? (
                <ManualPlatformGate
                  platforms={body.data.capabilities.platforms}
                  manualName={body.data.manual.name}
                  realm={session?.realm}
                  facilityId={exitFacilityId}
                  manualId={manualId}
                />
              ) : !started ? (
                <ManualStartPage
                  manual={body.data.manual}
                  ratings={ratings.data ?? null}
                  ratingsLoading={ratings.isPending}
                  hasSteps={allSteps.length > 0}
                  onStart={() => setStarted(true)}
                />
              ) : finished ? (
                // Gate the rate-on-finish panel until ratings settle: it seeds its form from `own` ONCE at mount, so
                // mounting while the fetch is pending would seed an empty comment and a later submit could ERASE a saved
                // one (panel). By finish the fetch is almost always done; the spinner is a safety net.
                ratings.isPending ? (
                  <p className="text-muted-foreground p-6 text-sm">{t("common.loading")}</p>
                ) : (
                <RateOnFinishPanel
                  session={session}
                  manualId={manualId}
                  canRate={body.data.capabilities.canRate}
                  ratings={ratings.data ?? null}
                  ratingsError={ratings.isError}
                  onBackToSteps={() => setFinished(false)}
                  // Exit to the manual's facility manuals list (same as the header's Back), not the raw fallback → root.
                  onClose={handleExit}
                />
                )
              ) : currentStep ? (
                <StepView
                  // Key by step id so navigating between two SAME-TYPE steps remounts the player — otherwise the
                  // interactive state (selected answer / checked items) leaks across steps (panel finding).
                  key={currentStep.id}
                  step={currentStep}
                  index={currentIndex}
                  total={allSteps.length}
                  onPrev={currentIndex > 0 ? () => setCurrentStepId(allSteps[currentIndex - 1]!.id) : undefined}
                  onNext={currentIndex < allSteps.length - 1 ? () => setCurrentStepId(allSteps[currentIndex + 1]!.id) : undefined}
                  onFinish={currentIndex === allSteps.length - 1 ? () => setFinished(true) : undefined}
                />
              ) : (
                <p className="text-muted-foreground p-6 text-sm">{t("viewer.noStepsInManual")}</p>
              )}
            </div>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
